import { useState, useEffect } from "react"
import Navbar from "../../components/Navbar"
import PageTitle from "../../components/PageTitle"
import { allFirstDiv } from "../../components/DesignStandardize"

// Import Google Fonts
const googleFonts = document.createElement('link')
googleFonts.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap'
googleFonts.rel = 'stylesheet'
document.head.appendChild(googleFonts)

const UserLocationCRUD = () => {
  const [locations, setLocations] = useState([])
  const [employees, setEmployees] = useState([])
  const [newLocation, setNewLocation] = useState({
    EmployeeID: "",
    Latitude: "",
    Longitude: "",
    LocationName: "",
    Radius: ""
  })
  const [editingLocation, setEditingLocation] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [employeeError, setEmployeeError] = useState(null)
  const [isMapOpen, setIsMapOpen] = useState(false)
  const [geolocationError, setGeolocationError] = useState(null)
  const [viewMode, setViewMode] = useState("card")

  const apiUrl = process.env.REACT_APP_API_BASE_URL

  useEffect(() => {
    fetchLocations()
    fetchEmployees()
  }, [])

  const fetchLocations = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`${apiUrl}/user-location/all`, headers())
      if (!response.ok) throw new Error(`Failed to fetch locations: ${response.statusText}`)
      const data = await response.json()
      if (!Array.isArray(data.locations)) {
        console.warn("API response 'locations' is not an array:", data)
        setLocations([])
        setError("Received invalid data format")
      } else {
        setLocations(data.locations)
      }
    } catch (err) {
      setError(`Failed to load locations: ${err.message}`)
      setLocations([])
      console.error("Error fetching locations:", err)
    } finally {
      setLoading(false)
    }
  }

  const fetchEmployees = async () => {
    try {
      const response = await fetch(`${apiUrl}/employee/all`, headers())
      if (!response.ok) throw new Error(`Failed to fetch employees: ${response.statusText}`)
      const data = await response.json()
      if (!Array.isArray(data.employeeInfos)) {
        console.warn("API response 'employeeInfos' is not an array:", data)
        setEmployees([])
        setEmployeeError("Received invalid employee data")
      } else {
        setEmployees(data.employeeInfos)
      }
    } catch (err) {
      setEmployeeError(`Failed to load employees: ${err.message}`)
      setEmployees([])
      console.error("Error fetching employees:", err)
    }
  }

  const headers = () => ({
    headers: { Authorization: `${localStorage.getItem("jwtToken")}` }
  })

  const handleInputChange = (e) => {
    const { name, value } = e.target
    if (editingLocation) {
      setEditingLocation({ ...editingLocation, [name]: value })
    } else {
      setNewLocation({ ...newLocation, [name]: value })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    try {
      if (editingLocation) {
        await updateLocation(editingLocation)
      } else {
        await createLocation(newLocation)
      }
      fetchLocations()
      setNewLocation({ EmployeeID: "", Latitude: "", Longitude: "", LocationName: "", Radius: "" })
      setEditingLocation(null)
      setIsMapOpen(false)
    } catch (err) {
      setError(`Failed to save location: ${err.message}`)
      console.error("Error saving location:", err)
    } finally {
      setLoading(false)
    }
  }

  const createLocation = async (location) => {
    const response = await fetch(`${apiUrl}/user-location/create`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `${localStorage.getItem("jwtToken")}` },
      body: JSON.stringify({
        EmployeeID: parseInt(location.EmployeeID),
        Latitude: parseFloat(location.Latitude),
        Longitude: parseFloat(location.Longitude),
        LocationName: location.LocationName,
        Radius: parseInt(location.Radius)
      })
    })
    if (!response.ok) throw new Error(`Failed to create location: ${response.statusText}`)
  }

  const updateLocation = async (location) => {
    const response = await fetch(`${apiUrl}/user-location/${location.LocationID}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `${localStorage.getItem("jwtToken")}` },
      body: JSON.stringify({
        Latitude: parseFloat(location.Latitude),
        Longitude: parseFloat(location.Longitude),
        LocationName: location.LocationName,
        Radius: parseInt(location.Radius)
      })
    })
    if (!response.ok) throw new Error(`Failed to update location: ${response.statusText}`)
  }

  const deleteLocation = async (id) => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`${apiUrl}/user-location/${id}`, {
        method: "DELETE",
        headers: { Authorization: `${localStorage.getItem("jwtToken")}` }
      })
      if (!response.ok) throw new Error(`Failed to delete location: ${response.statusText}`)
      fetchLocations()
    } catch (err) {
      setError(`Failed to delete location: ${err.message}`)
      console.error("Error deleting location:", err)
    } finally {
      setLoading(false)
    }
  }

  const toggleMap = () => setIsMapOpen(!isMapOpen)

  const getCurrentLocation = () => {
    setGeolocationError(null)
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords
          if (editingLocation) {
            setEditingLocation({ ...editingLocation, Latitude: latitude.toFixed(6), Longitude: longitude.toFixed(6) })
          } else {
            setNewLocation({ ...newLocation, Latitude: latitude.toFixed(6), Longitude: longitude.toFixed(6) })
          }
        },
        (error) => {
          let errorMessage
          switch (error.code) {
            case error.PERMISSION_DENIED: errorMessage = "Geolocation permission denied."; break
            case error.POSITION_UNAVAILABLE: errorMessage = "Location unavailable."; break
            case error.TIMEOUT: errorMessage = "Location request timed out."; break
            default: errorMessage = "Unknown error occurred."; break
          }
          setGeolocationError(errorMessage)
          console.error("Geolocation error:", error)
        }
      )
    } else {
      setGeolocationError("Geolocation not supported.")
    }
  }

  // Function to get employee initials
  const getInitials = (name) => {
    if (!name) return "N/A"
    const words = name.split(" ")
    return words.length > 1
      ? `${words[0][0]}${words[1][0]}`
      : words[0].slice(0, 2)
  }

  return (
    <>
      <style>{`
        .glassmorphism {
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        .shadow-premium {
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1), 0 3px 10px rgba(0, 0, 0, 0.05);
        }
        .button-gradient {
          background: linear-gradient(135deg, #2D2D2D, #3A3A3A);
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .button-gradient:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
        }
        .button-gradient:active {
          transform: scale(0.98);
        }
        .avatar-gradient {
          background: linear-gradient(135deg, #4A4A4A, #2D2D2D);
        }
      `}</style>
      <Navbar />
      <PageTitle title="User Location Management" />
      <div className={`${allFirstDiv} min-h-screen bg-[#F5F5F5] py-6 px-4 font-['Inter']`}>
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-[#2D2D2D] tracking-tight">Location Management</h1>
            <div className="space-x-2">
              <button
                onClick={() => setViewMode("card")}
                className={`px-3 py-1.5 rounded-md text-sm ${viewMode === "card" ? "bg-[#2D2D2D] text-white" : "bg-gray-200 text-gray-700"} hover:bg-gray-300 transition-colors`}
              >
                Card View
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`px-3 py-1.5 rounded-md text-sm ${viewMode === "list" ? "bg-[#2D2D2D] text-white" : "bg-gray-200 text-gray-700"} hover:bg-gray-300 transition-colors`}
              >
                List View
              </button>
            </div>
          </div>

          {/* CRUD Card */}
          <div className="glassmorphism rounded-xl shadow-premium p-5">
            <h2 className="text-lg font-medium text-[#2D2D2D] mb-4 tracking-tight">
              {editingLocation ? "Edit Location" : "Add Location"}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="EmployeeID" className="block text-sm font-medium text-gray-600">
                  Employee
                </label>
                <select
                  id="EmployeeID"
                  name="EmployeeID"
                  value={editingLocation ? editingLocation.EmployeeID : newLocation.EmployeeID}
                  onChange={handleInputChange}
                  required
                  disabled={editingLocation !== null}
                  className="mt-1 block w-full px-3 py-2 border border-gray-100 rounded-md bg-white/70 focus:outline-none focus:ring-1 focus:ring-[#2D2D2D] transition-all duration-200 text-sm"
                >
                  <option value="" disabled>Select employee</option>
                  {employees.map((employee) => (
                    <option key={employee.EmployeeID} value={employee.EmployeeID}>
                      {employee.Name} ({employee.Designation})
                    </option>
                  ))}
                </select>
                {employeeError && (
                  <p className="text-red-500 text-xs mt-1 flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    {employeeError}
                  </p>
                )}
              </div>

              <button
                type="button"
                onClick={toggleMap}
                className="w-full px-3 py-2 button-gradient text-white rounded-md flex items-center justify-between text-sm font-medium focus:outline-none focus:ring-1 focus:ring-[#2D2D2D] transition-all duration-200"
              >
                <span>{isMapOpen ? "Hide Map" : "Select from Map"}</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={isMapOpen ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"}/>
                </svg>
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${isMapOpen ? 'max-h-[400px]' : 'max-h-0'}`}>
                <div className="mt-2 p-3 bg-white/50 glassmorphism rounded-md shadow-premium">
                  <p className="text-xs text-gray-500 mb-2">Click map to get coordinates, then enter below.</p>
                  <iframe
                    src="https://www.gps-coordinates.net/"
                    width="100%"
                    height="350"
                    frameBorder="0"
                    title="GPS Coordinates Map"
                    className="rounded-md"
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={getCurrentLocation}
                className="w-full px-3 py-2 button-gradient text-white rounded-md flex items-center justify-center text-sm font-medium focus:outline-none focus:ring-1 focus:ring-[#2D2D2D] transition-all duration-200"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
                Use Current Location
              </button>
              {geolocationError && (
                <p className="text-red-500 text-xs mt-1 flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  {geolocationError}
                </p>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="Latitude" className="block text-sm font-medium text-gray-600">
                    Latitude
                  </label>
                  <input
                    type="number"
                    step="any"
                    id="Latitude"
                    name="Latitude"
                    value={editingLocation ? editingLocation.Latitude : newLocation.Latitude}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full px-3 py-2 border border-gray-100 rounded-md bg-white/70 focus:outline-none focus:ring-1 focus:ring-[#2D2D2D] transition-all duration-200 text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="Longitude" className="block text-sm font-medium text-gray-600">
                    Longitude
                  </label>
                  <input
                    type="number"
                    step="any"
                    id="Longitude"
                    name="Longitude"
                    value={editingLocation ? editingLocation.Longitude : newLocation.Longitude}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full px-3 py-2 border border-gray-100 rounded-md bg-white/70 focus:outline-none focus:ring-1 focus:ring-[#2D2D2D] transition-all duration-200 text-sm"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="LocationName" className="block text-sm font-medium text-gray-600">
                  Location Name
                </label>
                <input
                  type="text"
                  id="LocationName"
                  name="LocationName"
                  value={editingLocation ? editingLocation.LocationName : newLocation.LocationName}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-100 rounded-md bg-white/70 focus:outline-none focus:ring-1 focus:ring-[#2D2D2D] transition-all duration-200 text-sm"
                />
              </div>
              <div>
                <label htmlFor="Radius" className="block text-sm font-medium text-gray-600">
                  Radius (m)
                </label>
                <input
                  type="number"
                  id="Radius"
                  name="Radius"
                  value={editingLocation ? editingLocation.Radius : newLocation.Radius}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-100 rounded-md bg-white/70 focus:outline-none focus:ring-1 focus:ring-[#2D2D2D] transition-all duration-200 text-sm"
                />
              </div>
              <button
                type="submit"
                disabled={loading || employees.length === 0}
                className="w-full px-3 py-2 button-gradient text-white rounded-md flex items-center justify-center text-sm font-medium focus:outline-none focus:ring-1 focus:ring-[#2D2D2D] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {loading ? (
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8h8a8 8 0 01-16 0z"/>
                  </svg>
                ) : (
                  <span>{editingLocation ? "Update Location" : "Add Location"}</span>
                )}
              </button>
              {error && (
                <p className="text-red-500 text-xs mt-1 flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  {error}
                </p>
              )}
            </form>
          </div>

          {/* Locations List/Card View */}
          <div className="glassmorphism rounded-xl shadow-premium p-5">
            <h2 className="text-lg font-medium text-[#2D2D2D] mb-4 tracking-tight">Locations</h2>
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <svg className="animate-spin h-6 w-6 text-[#2D2D2D]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8h8a8 8 0 01-16 0z"/>
                </svg>
              </div>
            ) : locations.length > 0 ? (
              <div className="space-y-4">
                {viewMode === "card" ? (
                  locations.map((location) => (
                    <div key={location.LocationID} className="glassmorphism rounded-xl p-4 shadow-premium hover:shadow-xl transition-all duration-200">
                      <div className="flex items-center space-x-4">
                        <div className="w-14 h-14 avatar-gradient rounded-full flex items-center justify-center text-white font-medium text-lg">
                          {getInitials(location.EmployeeInfo?.Name)}
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between items-center">
                            <div>
                              <h3 className="text-sm font-medium text-[#2D2D2D]">{location.EmployeeInfo?.Name || 'N/A'}</h3>
                              <p className="text-xs text-gray-600">Role: {location.EmployeeInfo?.Designation || 'N/A'}</p>
                            </div>
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">Active</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 space-y-1">
                        <p className="text-xs text-gray-600">Location: {location.LocationName}</p>
                        <p className="text-xs text-gray-600">Lat: {location.Latitude}, Long: {location.Longitude}</p>
                        <p className="text-xs text-gray-600">Radius: {location.Radius}m</p>
                      </div>
                      <div className="flex justify-end space-x-2 mt-3">
                        <button
                          onClick={() => setEditingLocation(location)}
                          className="p-2 bg-indigo-50 text-indigo-600 rounded-full hover:bg-indigo-100 transition-all duration-200"
                          title="Edit"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/>
                          </svg>
                        </button>
                        <button
                          onClick={() => deleteLocation(location.LocationID)}
                          className="p-2 bg-red-50 text-red-600 rounded-full hover:bg-red-100 transition-all duration-200"
                          title="Delete"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead className="bg-white/60 glassmorphism">
                        <tr>
                          {["Employee", "Latitude", "Longitude", "Location", "Radius", "Actions"].map((header) => (
                            <th key={header} className="px-4 py-2 text-left text-xs font-medium text-gray-500 tracking-tight">
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {locations.map((location) => (
                          <tr key={location.LocationID} className="hover:bg-white/20 transition-all duration-200">
                            <td className="px-4 py-2 text-sm text-gray-700">
                              <div className="flex items-center space-x-2">
                                <div className="w-8 h-8 avatar-gradient rounded-full flex items-center justify-center text-white text-xs font-medium">
                                  {getInitials(location.EmployeeInfo?.Name)}
                                </div>
                                <span>{location.EmployeeInfo?.Name || 'N/A'}</span>
                              </div>
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-700">{location.Latitude}</td>
                            <td className="px-4 py-2 text-sm text-gray-700">{location.Longitude}</td>
                            <td className="px-4 py-2 text-sm text-gray-700">{location.LocationName}</td>
                            <td className="px-4 py-2 text-sm text-gray-700">{location.Radius}</td>
                            <td className="px-4 py-2 text-sm">
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => setEditingLocation(location)}
                                  className="p-2 bg-indigo-50 text-indigo-600 rounded-full hover:bg-indigo-100 transition-all duration-200"
                                  title="Edit"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/>
                                  </svg>
                                </button>
                                <button
                                  onClick={() => deleteLocation(location.LocationID)}
                                  className="p-2 bg-red-50 text-red-600 rounded-full hover:bg-red-100 transition-all duration-200"
                                  title="Delete"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-sm text-center py-4">No locations available</p>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default UserLocationCRUD